/**
 * ErrorLogViewer - Owner/Admin Dashboard for API Error Monitoring
 * 
 * Features:
 * - View recent API errors with filtering
 * - Error summary statistics
 * - Mark errors as resolved with notes
 * - Pagination support
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/TestAuthContext';
import { api } from '../utils/api';
import { 
  AlertTriangle, CheckCircle, XCircle, RefreshCw, Filter, 
  ChevronDown, ChevronUp, ExternalLink, Clock, Server,
  TrendingUp, AlertOctagon, Check, X, MessageSquare
} from 'lucide-react';

interface ErrorLog {
  id?: string;
  status: number;
  message: string;
  endpoint: string;
  timestamp: string;
  details?: any;
  user_agent?: string;
  url?: string;
  user_email?: string;
  org_id?: string;
  resolved: boolean;
  resolved_at?: string;
  resolved_by?: string;
  notes?: string;
  created_at?: string;
}

interface ErrorSummary {
  total_errors: number;
  unresolved_errors: number;
  resolved_errors: number;
  status_breakdown: Array<{ status: number; count: number }>;
  top_endpoints: Array<{ endpoint: string; count: number }>;
  recent_errors: ErrorLog[];
  period_days: number;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ErrorLogViewer() {
  const { getToken } = useAuth();
  
  // State
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [summary, setSummary] = useState<ErrorSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<number | null>(null);
  const [resolvedFilter, setResolvedFilter] = useState<boolean | null>(null);
  const [daysFilter, setDaysFilter] = useState(7);
  
  // Pagination
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;
  
  // Expanded error details
  const [expandedId, setExpandedId] = useState<string | null>(null);
  
  // Resolve modal
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [resolveNotes, setResolveNotes] = useState('');

  // Fetch error summary
  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const token = await getToken();
      const data = await api.get(
        `/api/v1/errors/summary?days=${daysFilter}`,
        { headers: { Authorization: `Bearer ${token}` }, showErrorToast: false }
      );
      setSummary(data);
    } catch (err: any) {
      console.error('Failed to fetch summary:', err);
      if (err.status === 403) {
        setError('You do not have permission to view error logs');
      }
    } finally {
      setSummaryLoading(false);
    }
  }, [getToken, daysFilter]);

  // Fetch error list
  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      
      let url = `/api/v1/errors/list?limit=${limit}&offset=${offset}&days=${daysFilter}`;
      if (statusFilter !== null) url += `&status_filter=${statusFilter}`;
      if (resolvedFilter !== null) url += `&resolved=${resolvedFilter}`;
      
      const data = await api.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      
      setErrors(data.errors || []);
      setTotal(data.total || 0);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch errors:', err);
      if (err.status === 403) {
        setError('You do not have permission to view error logs');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [getToken, offset, daysFilter, statusFilter, resolvedFilter]);

  // Resolve error
  const resolveError = async (errorId: string) => {
    try {
      const token = await getToken();
      await api.put(
        `/api/v1/errors/${errorId}/resolve`,
        { notes: resolveNotes || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh data
      fetchErrors();
      fetchSummary();
      setResolvingId(null);
      setResolveNotes('');
    } catch (err) {
      console.error('Failed to resolve error:', err);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchSummary();
    fetchErrors();
  }, [fetchSummary, fetchErrors]);

  // Get status color
  const getStatusColor = (status: number) => {
    if (status >= 500) return 'text-red-600 bg-red-50 border-red-200';
    if (status >= 400) return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-blue-600 bg-blue-50 border-blue-200';
  };

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (error && !errors.length) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-red-800 mb-2">Access Denied</h3>
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{summary.total_errors}</p>
                <p className="text-sm text-gray-500">Total Errors ({summary.period_days}d)</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertOctagon className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{summary.unresolved_errors}</p>
                <p className="text-sm text-gray-500">Unresolved</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{summary.resolved_errors}</p>
                <p className="text-sm text-gray-500">Resolved</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Server className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{summary.top_endpoints.length}</p>
                <p className="text-sm text-gray-500">Affected Endpoints</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Breakdown */}
      {summary && summary.status_breakdown.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Error Distribution by Status</h3>
          <div className="flex flex-wrap gap-2">
            {summary.status_breakdown.map(({ status, count }) => (
              <button
                key={status}
                onClick={() => setStatusFilter(statusFilter === status ? null : status)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${
                  statusFilter === status
                    ? 'bg-blue-600 text-white border-blue-600'
                    : getStatusColor(status)
                }`}
              >
                {status}: {count}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>
          
          <select
            value={daysFilter}
            onChange={(e) => { setDaysFilter(Number(e.target.value)); setOffset(0); }}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          
          <select
            value={resolvedFilter === null ? '' : String(resolvedFilter)}
            onChange={(e) => { 
              setResolvedFilter(e.target.value === '' ? null : e.target.value === 'true');
              setOffset(0);
            }}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
          >
            <option value="">All Status</option>
            <option value="false">Unresolved</option>
            <option value="true">Resolved</option>
          </select>
          
          {statusFilter && (
            <button
              onClick={() => setStatusFilter(null)}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm flex items-center gap-1"
            >
              Status: {statusFilter} <X className="w-3 h-3" />
            </button>
          )}
          
          <button
            onClick={() => { fetchErrors(); fetchSummary(); }}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm flex items-center gap-1 ml-auto"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Error Log</h3>
            <span className="text-sm text-gray-500">
              {offset + 1}-{Math.min(offset + limit, total)} of {total}
            </span>
          </div>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-2" />
            <p className="text-gray-500">Loading errors...</p>
          </div>
        ) : errors.length === 0 ? (
          <div className="p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No Errors Found</h3>
            <p className="text-gray-500">Great! No API errors in the selected period.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {errors.map((err, idx) => (
              <div key={err.id || idx} className="hover:bg-gray-50 transition">
                {/* Error Row */}
                <div 
                  className="px-4 py-3 cursor-pointer"
                  onClick={() => setExpandedId(expandedId === (err.id || String(idx)) ? null : (err.id || String(idx)))}
                >
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${getStatusColor(err.status)}`}>
                      {err.status}
                    </span>
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{err.endpoint}</p>
                      <p className="text-sm text-gray-500 truncate">{err.message}</p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {err.resolved ? (
                        <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                          <CheckCircle className="w-3 h-3" /> Resolved
                        </span>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); setResolvingId(err.id || String(idx)); }}
                          className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Resolve
                        </button>
                      )}
                      
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {formatDate(err.timestamp || err.created_at || '')}
                      </span>
                      
                      {expandedId === (err.id || String(idx)) ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Expanded Details */}
                {expandedId === (err.id || String(idx)) && (
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500 mb-1">User</p>
                        <p className="font-medium text-gray-900">{err.user_email || 'Unknown'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-1">Page URL</p>
                        <p className="font-medium text-gray-900 truncate">{err.url || 'N/A'}</p>
                      </div>
                      {err.details && (
                        <div className="col-span-2">
                          <p className="text-gray-500 mb-1">Error Details</p>
                          <pre className="bg-gray-100 rounded p-2 text-xs overflow-x-auto">
                            {JSON.stringify(err.details, null, 2)}
                          </pre>
                        </div>
                      )}
                      {err.resolved && err.notes && (
                        <div className="col-span-2">
                          <p className="text-gray-500 mb-1">Resolution Notes</p>
                          <p className="bg-green-50 border border-green-200 rounded p-2 text-green-800">
                            {err.notes}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Resolved by {err.resolved_by} on {formatDate(err.resolved_at || '')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Resolve Modal */}
                {resolvingId === (err.id || String(idx)) && (
                  <div className="px-4 py-3 bg-blue-50 border-t border-blue-200">
                    <div className="flex items-start gap-3">
                      <MessageSquare className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-blue-900 mb-2">Mark as Resolved</p>
                        <textarea
                          value={resolveNotes}
                          onChange={(e) => setResolveNotes(e.target.value)}
                          placeholder="Add notes about the fix (optional)..."
                          className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm"
                          rows={2}
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => resolveError(err.id || String(idx))}
                            className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm flex items-center gap-1"
                          >
                            <Check className="w-4 h-4" /> Confirm
                          </button>
                          <button
                            onClick={() => { setResolvingId(null); setResolveNotes(''); }}
                            className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Pagination */}
        {total > limit && (
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            <button
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
              className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm disabled:opacity-50"
            >
              ← Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {Math.floor(offset / limit) + 1} of {Math.ceil(total / limit)}
            </span>
            <button
              onClick={() => setOffset(offset + limit)}
              disabled={offset + limit >= total}
              className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm disabled:opacity-50"
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
